import React, {
  ForwardedRef,
  RefObject,
  createContext,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import { TabListAria } from '@react-aria/tabs';
import { filterDOMProps, mergeProps, useObjectRef } from '@react-aria/utils';
import { DOMProps } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/tabs/tabs.module.css';

import OverflowWrapper from './OverflowWrapper';
import { UnderlinedTabsProps } from '../TabsProps';
import { useHasOverflow } from './useHasOverflow';
import { useScrollableTabs } from './useScrollableTabs';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../../utils';
import useAnimatedSegment from '../../../utils/useAnimatedSegment';

export interface UnderlinedTabListProps
  extends UnderlinedTabsProps,
    SapphireStyleProps,
    DOMProps {
  children: React.ReactNode;
  tabListProps?: TabListAria['tabListProps'];
}

export type TabElementType = HTMLDivElement | HTMLAnchorElement;

export interface TabsContextValue {
  registerTab: (tabRef: RefObject<TabElementType>) => void;
  deregisterTab: (tabRef: RefObject<TabElementType>) => void;
  setActiveTabRef: (tabRef: RefObject<TabElementType>) => void;
  intersectionObserver: IntersectionObserver | null;
  scrollTabIntoView: (tabElement: TabElementType, isSmooth?: boolean) => void;
}

export const TabsContext = createContext<TabsContextValue | null>(null);

export const UnderlinedTabsList = forwardRef(function UnderlinedNavTabs(
  { children, align = 'left', ...props }: UnderlinedTabListProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const tabListRef = useObjectRef(ref);
  const { hideBorderBottom, isInline, size = 'md', tabListProps } = props;
  useThemeCheck();

  const { styleProps } = useSapphireStyleProps(props);
  const domProps = filterDOMProps(props, { global: true });

  const [tabRefs, setTabRefs] = useState<RefObject<TabElementType>[]>([]);
  const [activeTabRef, setActiveTabRef] = useState<RefObject<TabElementType>>({
    current: null,
  });

  const { hasOverflow, onScroll } = useHasOverflow({ ref: tabListRef });

  const tabsContainerRef = React.useRef<HTMLDivElement>(null);

  const { canAnimate, position } = useAnimatedSegment(
    {
      containerRef: tabsContainerRef,
      activeElementRef: activeTabRef,
      onComputeRequest: (element) => {
        const tabsContainer = tabsContainerRef.current;
        if (!tabsContainer) return { width: 0, offsetLeft: 0 };

        const tabsContainerRect = tabsContainer.getBoundingClientRect();
        const activeTabRect = element.getBoundingClientRect();

        // Get the tab's horizontal padding to calculate underline positioning
        const tabComputedStyle = getComputedStyle(element);
        const tabPaddingLeft = parseInt(tabComputedStyle.paddingLeft) || 0;
        const tabPaddingRight = parseInt(tabComputedStyle.paddingRight) || 0;

        const offsetLeft =
          activeTabRect.left -
          tabsContainerRect.left +
          tabsContainer.scrollLeft +
          tabPaddingLeft;

        // Calculate width and position based on actual tab padding
        const width = activeTabRect.width - (tabPaddingLeft + tabPaddingRight);

        return { width, offsetLeft };
      },
    },
    [align]
  );

  const registerTab = (tabRef: RefObject<TabElementType>) => {
    setTabRefs((prevTabRefs) => [...prevTabRefs, tabRef]);
  };

  const deregisterTab = (tabRef: RefObject<TabElementType>) => {
    setTabRefs((prevTabRefs) => prevTabRefs.filter((ref) => ref !== tabRef));
  };

  const { scrollToDirection, intersectionObserver, scrollTabIntoView } =
    useScrollableTabs({
      tabRefs: tabRefs,
      scrollContainerRef: tabListRef,
    });

  return (
    <TabsContext.Provider
      value={{
        registerTab,
        deregisterTab,
        setActiveTabRef,
        intersectionObserver,
        scrollTabIntoView,
      }}
    >
      <OverflowWrapper
        isOverflowLeft={hasOverflow.left}
        isOverflowRight={hasOverflow.right}
        onClickLeft={() => scrollToDirection('left')}
        onClickRight={() => scrollToDirection('right')}
        size={size}
        isInline={isInline}
        {...mergeProps(styleProps, domProps)}
      >
        <div
          ref={tabListRef}
          onScroll={onScroll}
          className={clsx(styles['sapphire-tabs-scroll-container'])}
        >
          <div
            {...tabListProps}
            ref={tabsContainerRef}
            className={clsx(
              styles['sapphire-tabs'],
              [styles[`sapphire-tabs--align-${align}` as const]],
              {
                [styles[`sapphire-tabs--no-border`]]: hideBorderBottom,
                [styles[`sapphire-tabs--sm`]]: size === 'sm',
                [styles[`sapphire-tabs--lg`]]: size === 'lg',
              }
            )}
          >
            {children}
          </div>

          <div
            className={clsx(styles['sapphire-tabs__underline'], {
              [styles['sapphire-tabs__underline--with-transition']]: canAnimate,
            })}
            style={{
              width: position.width,
              transform: `translateX(${position.offsetLeft}px)`,
            }}
          />
        </div>
      </OverflowWrapper>
    </TabsContext.Provider>
  );
});

export const useIntersectionObserver = (
  ref: React.RefObject<HTMLDivElement | HTMLAnchorElement>,
  intersectionObserver: IntersectionObserver | null
): void => {
  useEffect(() => {
    const element = ref.current;
    if (element) {
      intersectionObserver?.observe(element);
      return () => {
        intersectionObserver?.unobserve(element);
      };
    }
  }, [intersectionObserver]);
};
